<template>
  <section>
    <template v-if="hasLink">
      <NuxtLink
        :to="link?.href"
        class="group"
        :external="link?.external"
        :target="link?.external ? '_blank' : ''"
      >
        <picture v-if="props.image">
          <source
            v-for="(source, index) in props.image.mediaImageSource"
            :key="index"
            :srcset="source.srcset"
            :media="source.media"
            :type="source.type"
            :width="source.width"
            :height="source.height"
          />
          <img
            :src="props.image.mediaImageUrl"
            :alt="props.image.mediaImageAlt"
            :title="props.image.mediaImageTitle || undefined"
            :width="props.image.mediaImageWidth"
            :height="props.image.mediaImageHeight"
            :fetchpriority="props.fetchPriority"
            class="w-full h-auto m-0 p-0 group-hover:brightness-80 transition-all duration-300 ease-in-out"
            :class="[hasContent ? 'mb-4' : '', props.imageClass]"
          />
        </picture>

        <h6
          v-if="props.title"
          class="text-h6 text-mbzuai-navy-500! text-underline-animation"
          :class="[textAlign === 'right' ? 'lg:text-right' : 'lg:text-left']"
        >
          {{ props.title }}
        </h6>

        <p
          v-if="props.caption"
          class="text-caption text-surface-500"
          :class="[
            textAlign === 'right' ? 'lg:text-right' : 'lg:text-left',
            props.description ? 'mb-4' : 'mt-2',
          ]"
        >
          <DrupalMarkup :content="props.caption" />
        </p>

        <p
          v-if="props.description"
          class="text-body-small text-surface-500 mt-4"
          :class="[textAlign === 'right' ? 'lg:text-right' : 'lg:text-left']"
        >
          <DrupalMarkup :content="props.description" />
        </p>
      </NuxtLink>
    </template>

    <template v-else>
      <picture v-if="props.image">
        <source
          v-for="(source, index) in props.image.mediaImageSource"
          :key="index"
          :srcset="source.srcset"
          :media="source.media"
          :type="source.type"
          :width="source.width"
          :height="source.height"
        />
        <img
          :src="props.image.mediaImageUrl"
          :alt="props.image.mediaImageAlt"
          :title="props.image.mediaImageTitle || undefined"
          :width="props.image.mediaImageWidth"
          :height="props.image.mediaImageHeight"
          :fetchpriority="props.fetchPriority"
          class="w-full h-auto"
          :class="[hasContent ? 'mb-4' : '', props.imageClass]"
        />
      </picture>

      <h6
        v-if="props.title"
        class="text-h6 m-0"
        :class="[textAlign === 'right' ? 'lg:text-right' : 'lg:text-left']"
      >
        {{ props.title }}
      </h6>

      <p
        v-if="props.caption"
        class="text-caption text-surface-500 mt-2"
        :class="[textAlign === 'right' ? 'lg:text-right' : 'lg:text-left']"
      >
        <DrupalMarkup :content="props.caption" />
      </p>

      <p
        v-if="props.description"
        class="text-body-small text-surface-500 mt-4"
        :class="[textAlign === 'right' ? 'lg:text-right' : 'lg:text-left']"
      >
        <DrupalMarkup :content="props.description" />
      </p>
    </template>
  </section>
</template>

<script setup lang="ts">
import type { Link, ResponsiveImage } from '~/types';

const props = withDefaults(
  defineProps<{
    caption?: string;
    description?: string | string[] | null;
    image?: ResponsiveImage;
    imageClass?: string;
    link?: Link;
    textAlign?: 'left' | 'right';
    title?: string;
    fetchPriority?: 'auto' | 'high' | 'low';
  }>(),
  {
    textAlign: 'left',
    fetchPriority: 'auto',
  }
);

const hasLink = computed(() => props.link?.href && props.link?.href.length > 1);
const hasContent = computed(() => {
  return Boolean(
    props.title?.length || props.caption?.length || props.description?.length
  );
});
</script>
